// File: src/pages/PartyLedgerSummaryAll.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Spinner, Button } from "reactstrap";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// shared imports (same style as your current page)
import { CreateCustomerList, _Get } from "../import/ApiEndPoint/ApiCalling";
import {
  Create_Account_List,
  Create_Transporter_List,
  View_Expense_Account,
  View_Ledger_by_id,
} from "../import/ApiEndPoint/Api";

const safeNum = (x) => (isNaN(Number(x)) ? 0 : Number(x));
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

function calcSummary(ledgerList = [], openingBalance = 0, openingType = "") {
  const type = toStr(openingType).toLowerCase();

  const txnDebit = ledgerList.reduce(
    (sum, row) => sum + safeNum(row?.debit),
    0,
  );
  const txnCredit = ledgerList.reduce(
    (sum, row) => sum + safeNum(row?.credit),
    0,
  );

  const obDebit = type === "debit" ? safeNum(openingBalance) : 0;
  const obCredit = type === "credit" ? safeNum(openingBalance) : 0;

  const totalDebit = txnDebit + obDebit;
  const totalCredit = txnCredit + obCredit;

  const closingDiff = Math.abs(totalDebit - totalCredit);

  // debit > credit => Dr
  // credit > debit => Cr
  let closingBalance = "0.00";
  if (closingDiff > 0) {
    const side = totalDebit > totalCredit ? "Dr" : "Cr";
    closingBalance = `${closingDiff.toFixed(2)} ${side}`;
  }

  return {
    totalDebit,
    totalCredit,
    closingDiff,
    closingBalance,
  };
}

// dynamic account type resolver for users
function getUserAccountType(userObj = {}) {
  const candidates = [
    userObj?.accountType,
    userObj?.AccountType,
    userObj?.userAccountType,
    userObj?.roleName,
    userObj?.role,
    userObj?.userRole,
    userObj?.designation,
    userObj?.departmentName,
    userObj?.typeName,
    userObj?.account,
    userObj?.rolename?.roleName,
    userObj?.rolename?.name,
    userObj?.rolename?.title,
    typeof userObj?.rolename === "string" ? userObj?.rolename : "",
  ];

  const found = candidates
    .map((x) => toStr(x).trim())
    .find(
      (x) => x && x.toLowerCase() !== "undefined" && x.toLowerCase() !== "null",
    );

  return found || "User";
}

export default function PartyLedgerSummaryAll() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const totals = useMemo(() => {
    const totalCredit = rows.reduce((sum, r) => sum + safeNum(r.credit), 0);
    const totalDebit = rows.reduce((sum, r) => sum + safeNum(r.debit), 0);
    const closingDiff = Math.abs(totalDebit - totalCredit);

    let closingBalance = "0.00";
    if (closingDiff > 0) {
      const side = totalDebit > totalCredit ? "Dr" : "Cr";
      closingBalance = `${closingDiff.toFixed(2)} ${side}`;
    }

    return { totalCredit, totalDebit, closingDiff, closingBalance };
  }, [rows]);

  const downloadExcel = () => {
    if (!rows.length) return;

    const aoa = [
      ["Party Ledger Summary"],
      [],
      ["Name", "Account Type", "Credit", "Debit", "Closing Balance"],
      ...rows.map((r) => [
        r.name,
        r.accountType,
        safeNum(r.credit).toFixed(2),
        safeNum(r.debit).toFixed(2),
        r.closingBalance,
      ]),
      [],
      [
        "TOTAL",
        "",
        totals.totalCredit.toFixed(2),
        totals.totalDebit.toFixed(2),
        totals.closingBalance,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!cols"] = [
      { wch: 35 }, // Name
      { wch: 25 }, // Account Type
      { wch: 15 }, // Credit
      { wch: 15 }, // Debit
      { wch: 20 }, // Closing Balance
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger Summary");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "Party_Ledger_Summary.xlsx",
    );
  };

  const downloadPDF = () => {
    if (!rows.length) return;

    const doc = new jsPDF("p", "mm", "a4");

    doc.setFontSize(14);
    doc.text("Party Ledger Summary", 14, 12);

    autoTable(doc, {
      startY: 18,
      head: [["Name", "Account Type", "Credit", "Debit", "Closing Balance"]],
      body: rows.map((r) => [
        r.name,
        r.accountType,
        safeNum(r.credit).toFixed(2),
        safeNum(r.debit).toFixed(2),
        r.closingBalance,
      ]),
      foot: [
        [
          "TOTAL",
          "",
          totals.totalCredit.toFixed(2),
          totals.totalDebit.toFixed(2),
          totals.closingBalance,
        ],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42] },
      footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] },
      columnStyles: {
        0: { cellWidth: 58 },
        1: { cellWidth: 42 },
        2: { halign: "right", cellWidth: 26 },
        3: { halign: "right", cellWidth: 26 },
        4: { halign: "right", cellWidth: 28 },
      },
      margin: { left: 10, right: 10 },
      didParseCell: (data) => {
        if (data.section === "head") {
          data.cell.styles.halign = data.column.index >= 2 ? "right" : "left";
        }
      },
    });

    doc.save("Party_Ledger_Summary.pdf");
  };

  useEffect(() => {
    let alive = true;

    const loadAll = async () => {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");

        // MASTER support (uses selected Superadmin if present)
        let sourceUserId = user?._id;
        let sourceDb = user?.database;

        if (user?.rolename?.roleName === "MASTER") {
          const picked = JSON.parse(
            localStorage.getItem("SuperadminIdByMaster") || "null",
          );
          if (picked) {
            const [id, db] = String(picked).split(" ");
            sourceUserId = id;
            sourceDb = db;
          }
        }

        const userListUrl = `${Create_Account_List}${sourceUserId}/`;

        // same sources as PartyLedgersView
        const [custRes, accRes, expRes, trRes] = await Promise.allSettled([
          CreateCustomerList(sourceUserId, sourceDb),
          _Get(userListUrl, sourceDb),
          _Get(View_Expense_Account, sourceDb),
          _Get(Create_Transporter_List, sourceDb),
        ]);

        const transporters = Array.isArray(trRes?.value?.Transporter)
          ? trRes.value.Transporter.map((t) => ({
              _id: t?._id,
              fullName: t?.companyName || "-",
              accountType: "Transporter",
              OpeningBalance: t?.OpeningBalance,
              Type: t?.Type,
            }))
          : [];

        const customers = Array.isArray(custRes?.value?.Customer)
          ? custRes.value.Customer.map((c) => ({
              _id: c?._id,
              fullName: c?.CompanyName || "-",
              accountType: "Customer",
              OpeningBalance: c?.OpeningBalance,
              Type: c?.Type,
            }))
          : [];

        const users = Array.isArray(accRes?.value?.adminDetails)
          ? accRes.value.adminDetails.map((u) => ({
              _id: u?._id,
              fullName:
                [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
                u?.firstName ||
                u?.name ||
                "-",
              accountType: getUserAccountType(u),
              OpeningBalance: u?.OpeningBalance,
              Type: u?.Type,
            }))
          : [];

        const expenses = Array.isArray(expRes?.value?.Expenses)
          ? expRes.value.Expenses.map((e) => ({
              _id: e?._id,
              fullName: `${e?.title || ""} ${e?.type || ""}`.trim() || "-",
              accountType: "Expense",
              OpeningBalance: e?.OpeningBalance,
              Type: e?.Type,
            }))
          : [];

        const parties = [...customers, ...users, ...expenses, ...transporters]
          .filter((p) => p?._id)
          .sort((a, b) =>
            toStr(a?.fullName).localeCompare(toStr(b?.fullName), undefined, {
              sensitivity: "base",
            }),
          );

        // fetch ledger for every party (batch to avoid too many parallel requests)
        const finalRows = [];
        const batchSize = 10;

        for (let i = 0; i < parties.length; i += batchSize) {
          const batch = parties.slice(i, i + batchSize);

          const batchResults = await Promise.allSettled(
            batch.map(async (p) => {
              try {
                const res = await _Get(View_Ledger_by_id, p._id);
                const ledgerList = Array.isArray(res?.Ledger) ? res.Ledger : [];

                const summary = calcSummary(
                  ledgerList,
                  p?.OpeningBalance,
                  p?.Type,
                );

                return {
                  name: p?.fullName || "-",
                  accountType: p?.accountType || "Account",
                  credit: summary.totalCredit,
                  debit: summary.totalDebit,
                  closingDiff: summary.closingDiff,
                  closingBalance: summary.closingBalance,
                };
              } catch (err) {
                // if no ledger/API fails, still consider opening balance
                const summary = calcSummary([], p?.OpeningBalance, p?.Type);

                return {
                  name: p?.fullName || "-",
                  accountType: p?.accountType || "Account",
                  credit: summary.totalCredit,
                  debit: summary.totalDebit,
                  closingDiff: summary.closingDiff,
                  closingBalance: summary.closingBalance,
                };
              }
            }),
          );

          batchResults.forEach((r) => {
            if (r.status === "fulfilled") finalRows.push(r.value);
          });
        }

        // hide zero closing rows
        const filteredRows = finalRows.filter(
          (r) => safeNum(r.closingDiff) > 0,
        );

        if (alive) setRows(filteredRows);
      } catch (e) {
        console.error(e);
        swal("Failed to load party ledger summary");
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadAll();

    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <Spinner style={{ width: "3rem", height: "3rem" }} color="primary" />
      </div>
    );
  }

  return (
    <Card
      style={{
        borderRadius: 12,
        border: "1px solid #e5e7eb",
      }}
    >
      <CardBody style={{ padding: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            Party Ledger Summary ({rows.length})
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              color="danger"
              onClick={downloadPDF}
              disabled={!rows.length}
              style={{ minWidth: 110 }}
            >
              PDF
            </Button>
            <Button
              color="primary"
              onClick={downloadExcel}
              disabled={!rows.length}
              style={{ minWidth: 110 }}
            >
              Excel
            </Button>
          </div>
        </div>

        <div style={{ width: "100%", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 900,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    fontWeight: 700,
                  }}
                >
                  Name
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    fontWeight: 700,
                  }}
                >
                  Account Type
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    fontWeight: 700,
                  }}
                >
                  Credit
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    fontWeight: 700,
                  }}
                >
                  Debit
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "10px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    fontWeight: 700,
                  }}
                >
                  Closing Balance
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      color: "#6b7280",
                    }}
                  >
                    No parties with non-zero closing balance.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.name}-${r.accountType}-${i}`}>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      {r.name}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      {r.accountType}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {safeNum(r.credit).toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {safeNum(r.debit).toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                      }}
                    >
                      {r.closingBalance}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td
                    style={{
                      padding: "10px 12px",
                      borderTop: "2px solid #cbd5e1",
                      background: "#f8fafc",
                      fontWeight: 700,
                    }}
                  >
                    TOTAL
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      borderTop: "2px solid #cbd5e1",
                      background: "#f8fafc",
                      fontWeight: 700,
                    }}
                  />
                  <td
                    style={{
                      padding: "10px 12px",
                      borderTop: "2px solid #cbd5e1",
                      background: "#f8fafc",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                    }}
                  >
                    {totals.totalCredit.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      borderTop: "2px solid #cbd5e1",
                      background: "#f8fafc",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                    }}
                  >
                    {totals.totalDebit.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      borderTop: "2px solid #cbd5e1",
                      background: "#f8fafc",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                    }}
                  >
                    {totals.closingBalance}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
